import type { BenefitState } from './fare_benefits.js';
import {
  FARE_DIMENSIONS,
  type FareAttributeState,
  type FareDimension,
  type FarePolarity,
  type FareTextClassifier,
  type FlightFareSource,
} from './fare_source.js';

/** O mínimo de uma tarifa pra entrar na régua de recomendação e nos estados por
 *  dimensão. Consumido pelos cards de família (`FareFamilyCard`, `FareRail`) e
 *  pela seleção (`fare_selection.ts`). */
export type LadderFare = Pick<
  FlightFareSource,
  'fareId' | 'name' | 'price' | 'benefits' | 'attributes'
>;

/** As CINCO dimensões, sempre — a não declarada é `unknown`. */
export type BenefitStates = Record<FareDimension, BenefitState>;

export type RecommendedReason = 'checked_included' | 'cheapest';

const STATUS_STATE: Record<'ok' | 'warn' | 'violated', BenefitState> = {
  ok: 'included',
  warn: 'fee',
  violated: 'absent',
};

/** Vocabulário do atributo tipado → estado do card. `unknown` não entra: não sobrepõe. */
const ATTRIBUTE_STATE: Record<Exclude<FareAttributeState, 'unknown'>, BenefitState> = {
  included: 'included',
  fee: 'fee',
  not_offered: 'absent',
};

/** O estado de UMA dimensão dentro de uma frase, cruzando o enum do wire com a
 *  polaridade do texto. O enum vale para a frase INTEIRA, e a frase pode tocar
 *  várias dimensões dizendo coisas opostas ("Bagagem de mão incluída, sem
 *  despacho" chega como `ok`): sem cruzar, o `ok` pintava o despacho de verde.
 *  A frase que se contradiz sobre a MESMA dimensão (`conflict`) não vira cor
 *  nenhuma. Nega esta dimensão e o enum também nega ⇒ ausência; nega e o enum
 *  afirma ⇒ contradição, que nunca vira cor. Afirma e o enum afirma ⇒ o enum;
 *  afirma, o enum NEGA e a frase toca mais de uma dimensão ⇒ a negativa pode ser
 *  de outra cláusula, então `unknown` — frase de uma dimensão só não tem esse
 *  risco e segue o enum. */
function resolvePolarity(
  phraseState: BenefitState,
  polarity: FarePolarity,
  dimCount: number,
): BenefitState {
  if (polarity === 'conflict') return 'unknown';
  if (polarity === 'negated') return phraseState === 'absent' ? 'absent' : 'unknown';
  return phraseState === 'absent' && dimCount > 1 ? 'unknown' : phraseState;
}

/**
 * O que a tarifa DIZ de cada dimensão. Regras, todas na direção de nunca afirmar
 * o que ninguém disse: `na` não conta (é o ramo default do mapper do
 * modal-session — provider sem availability reconhecível —, e a negativa de
 * verdade chega como `violated`); uma frase que toca várias dimensões declara
 * todas, cada uma com a SUA polaridade (`resolvePolarity`); duas frases em
 * desacordo sobre a mesma dimensão zeram a dimensão para `unknown`; o que não
 * foi declarado É `unknown` — não existe mais limiar que transforme omissão em
 * "sem X". O atributo estruturado (`attributes`), quando existe e não é
 * `unknown`, vence o texto do fornecedor — o texto é a inferência, o atributo
 * é a declaração. A frase é lida pelo `classify` do host (`polaritiesOf` do
 * domain).
 */
export function benefitStates(fare: LadderFare, classify: FareTextClassifier): BenefitStates {
  const declared = new Map<FareDimension, BenefitState>();
  for (const b of fare.benefits ?? []) {
    if (b.status === 'na') continue;
    const phraseState = STATUS_STATE[b.status];
    const dims = classify(b.text);
    for (const { dimension, polarity } of dims) {
      const state = resolvePolarity(phraseState, polarity, dims.length);
      const prev = declared.get(dimension);
      declared.set(dimension, prev === undefined || prev === state ? state : 'unknown');
    }
  }
  return Object.fromEntries(
    FARE_DIMENSIONS.map((d) => {
      const filed = fare.attributes?.[d];
      const fromAttribute = filed && filed !== 'unknown' ? ATTRIBUTE_STATE[filed] : undefined;
      return [d, fromAttribute ?? declared.get(d) ?? 'unknown'];
    }),
  ) as BenefitStates;
}

/** ALGUÉM declarou pelo menos uma dimensão — o fornecedor (`details[]` do
 *  tariff), com ou sem o mapa curado da VOLL por cima; `unknown` em todas é o
 *  mesmo que atributos ausentes para a voz do card — o servidor já garante isso
 *  (`supplierAttributesOf` não anexa `attributes` com as cinco `unknown`), mas
 *  o card não confia: um produtor mais antigo, ou outro produtor, ainda pode
 *  mandar. */
export function attributesDeclared(fare: LadderFare): boolean {
  return FARE_DIMENSIONS.some((d) => fare.attributes?.[d] && fare.attributes[d] !== 'unknown');
}

/** Tarifa recomendada: a mais barata com bagagem despachada DECLARADA como
 *  incluída; sem nenhuma, a mais barata. Determinística — nunca prosa de LLM. */
export function recommendedFare<T extends LadderFare>(
  fares: readonly T[],
  classify: FareTextClassifier,
): T | undefined {
  const byPrice = [...fares].sort((a, b) => a.price.amount - b.price.amount);
  return byPrice.find((f) => benefitStates(f, classify).checked === 'included') ?? byPrice[0];
}

/** Por que ESTA tarifa seria a recomendada, pela régua do `recommendedFare`.
 *  Fonte única do motivo: o trilho da página (que escolhe a recomendada card a
 *  card) pergunta aqui. */
export function recommendedReasonFor(
  fare: LadderFare,
  classify: FareTextClassifier,
): RecommendedReason {
  return benefitStates(fare, classify).checked === 'included' ? 'checked_included' : 'cheapest';
}
